import {observer} from "mobx-react-lite";
import React, {useCallback, useEffect, useRef} from "react";
import {reaction} from "mobx";
import {WindowStore} from "../../librettoEditor/store/WindowStore";
import {ApplicationStore, CropConfig} from "../../librettoEditor/store/ApplicationStore";
import {throttle} from "lodash-es/lodash";
import {calculateAspectRatioFit} from "../../librettoEditor/utils/math/calculateAspectRatioFit";
import {CompositionStateStore} from "../../librettoEditor/store/CompositionStateStore";
import {SelectionWatcher} from "../../librettoEditor/modules/composition/utils/CompositionSelectionWatcher";
import {CANVAS_ID, COMPOSITION_CONTAINER_ID} from "../../librettoEditor/config/config";
import DragResizeRotateContainer
    from "../../librettoEditor/modules/composition/containers/DragResizeRotateContainer/DragResizeRotateContainer";
import CropContainer
    from "../../librettoEditor/modules/composition/containers/CropContainer/CropContainer";
import {CompositionService} from "../../librettoEditor/services/CompositionService";
import {ScriptLayerClip, isScriptClip} from "../../engine/clips/script";
import {VideoClip} from "../../engine/clips/video/video";
import {ImageClip} from "../../engine/clips/image/image";

type CompositionCanvasSectionProps = {
    canvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
    containerRef: React.MutableRefObject<HTMLDivElement | null>;
    onCropDoneCapture?: (crop: CropConfig, scope: 'current' | 'all') => boolean | void;
};

// @ts-ignore
const CompositionCanvasSection = observer(({canvasRef, containerRef, onCropDoneCapture}: CompositionCanvasSectionProps) => {
    // Autorun for observing changes in MobX stores
    useEffect(() => {
        const disposer = reaction(
            () => ({
                canvasResolution: WindowStore.canvasResolution,
                selectedClipId: ApplicationStore.selectedClipId,
                displayWidth: CompositionStateStore.display.width,
                displayHeight: CompositionStateStore.display.height,
                windowResolution: WindowStore.resolution,
            }),
            () => {
                updateCanvasSize();
            });

        return () => {
            disposer(); // Dispose of the autorun when the component unmounts
        };
    }, []);

    // Handle ResizeObserver and MutationObserver
    useEffect(() => {
        if (containerRef.current) {
            // ResizeObserver for container size changes
            const resizeObserver = new ResizeObserver((entries) => {
                try {
                    for (const entry of entries) {
                        if (entry.target === containerRef.current) {
                            const {width, height} = entry.contentRect;
                            updateCanvasSize(); // Update canvas size based on container resize
                            WindowStore.setCanvasResolution([width, height]); // Sync with WindowStore
                        }
                    }
                } catch (e) {
                    console.error(e);
                }
            });
            resizeObserver.observe(containerRef.current);

            // MutationObserver for child DOM changes
            const mutationObserver = new MutationObserver((mutations) => {
                // Avoid re-initializing the canvas for unrelated subtree mutations (e.g. Moveable/handles during drag),
                // which can cause the canvas to "shake" due to repeated mount/scale writes.
                let canvasChanged = false;
                for (const m of mutations) {
                    const nodes = [...(m.addedNodes || []), ...(m.removedNodes || [])];
                    for (const n of nodes) {
                        if (!(n instanceof Element)) continue;
                        if ((n as HTMLElement).id === CANVAS_ID) {
                            canvasChanged = true;
                            break;
                        }
                        try {
                            if (typeof (n as Element).querySelector === 'function' && (n as Element).querySelector(`#${CANVAS_ID}`)) {
                                canvasChanged = true;
                                break;
                            }
                        } catch {}
                    }
                    if (canvasChanged) break;
                }
                if (canvasChanged) {
                    initializeCanvas();
                }
            });
            mutationObserver.observe(containerRef.current, {childList: true, subtree: true});

            // Initial canvas setup
            initializeCanvas();

            // Cleanup observers on component unmount
            return () => {
                resizeObserver.disconnect();
                mutationObserver.disconnect();
            };
        }
    }, [containerRef]);

    // Calculate the size for the canvas based on container dimensions
    const calculateWidthForComposition = () => {
        if (!containerRef.current || !canvasRef.current) return {newWidth: 0, newHeight: 0};

        const {clientWidth: containerWidth, clientHeight: containerHeight} = containerRef.current;
        const {width: canvasWidth, height: canvasHeight} = canvasRef.current;

        return calculateAspectRatioFit(canvasWidth, canvasHeight, containerWidth, containerHeight);
    };

    // Update the canvas size based on the container's dimensions
    const updateCanvasSize = useCallback(throttle(() => {
        if (canvasRef.current) {
            applyScale();
        }
    }, 100), []);

    // Initialize the canvas when the component mounts
    const initializeCanvas = () => {
        if (canvasRef.current) {
            const selectionWatcher = SelectionWatcher.getInstance();
            selectionWatcher.init(canvasRef.current);
            updateCanvasSize();
            CompositionService.mountComposition();
        }
    };

    // Handle mouse down event (deselect clip and visual element)
    const handleMouseDown = () => {
    };

    // Handle v application
    const handleApplyCrop = useCallback((crop: CropConfig, scope: 'current' | 'all') => {
        const selectedElement = ApplicationStore.selectedVisualElement;
        if (!selectedElement) {
            return;
        }

        const clipId = selectedElement.clipId;
        const sourceIndex = selectedElement.sourceIndex ?? 0;  // Default to 0 if not specified
        const clip = CompositionService.getClipById(clipId);

        if (clip && isScriptClip(clip)) {
            const scriptClip = clip as ScriptLayerClip;

            const normalizedCrop =
                crop.x === 0 && crop.y === 0 && crop.width === 1 && crop.height === 1
                    ? undefined
                    : { x: crop.x, y: crop.y, width: crop.width, height: crop.height };

            if (scope === 'current') {
                const absCompTime = Number(CompositionService.composition?.currentTime?.seconds ?? 0);
                const clipLocalTime = scriptClip.mapCompTimeToClipLocal(absCompTime);
                const sceneIndex = clipLocalTime != null ? scriptClip.findSceneIndexForTime(clipLocalTime) : 0;
                scriptClip.setSceneVisualProperties(sceneIndex, sourceIndex, {
                    crop: normalizedCrop,
                });
            } else {
                // Apply crop across all scenes by setting the source-level crop.
                try { scriptClip.clearSceneVisualOverrideKeysForSource(sourceIndex, ['crop']); } catch {}
                scriptClip.setVideoSourceCrop(sourceIndex, normalizedCrop);
            }
            try { CompositionService.notifyClipUpdated(scriptClip); } catch {}
        } else if (clip instanceof VideoClip || clip instanceof ImageClip) {
            // Apply crop to VideoClip
            if (crop.x === 0 && crop.y === 0 && crop.width === 1 && crop.height === 1) {
                clip.setCrop(undefined);
            } else {
                clip.setCrop({
                    x: crop.x,
                    y: crop.y,
                    width: crop.width,
                    height: crop.height,
                });
            }
            CompositionService.composition.computeFrame();
            try { CompositionService.notifyClipUpdated(clip); } catch {}
        } else {
            console.warn('[CompositionCanvasSection] Could not find clip with id:', clipId);
        }
    }, []);

    const applyScale = () => {
        if (CompositionService.isInitialized() === false) return;

        const comp = CompositionService.composition;
        const baseW = comp.settings.width;
        const baseH = comp.settings.height;

        const container = containerRef.current!;
        const canvas = canvasRef.current!;

        // Desired visual padding around the canvas (in CSS px)
        const pad = 0;

        // If container isn't laid out yet, don't apply scale(0) (it makes the canvas "disappear").
        const containerW = container.clientWidth;
        const containerH = container.clientHeight;
        if (!containerW || !containerH || baseW <= 0 || baseH <= 0) {
            return;
        }

        // 1) Fix the canvas CSS size to the render resolution (once)
        canvas.style.width = `${baseW}px`;
        canvas.style.height = `${baseH}px`;

        // 2) Compute scale to fit container
        const effectiveW = Math.max(0, containerW);
        const effectiveH = Math.max(0, containerH);
        const scale = Math.min(effectiveW / baseW, effectiveH / baseH);
        if (!Number.isFinite(scale) || scale <= 0) return;

        // 3) Apply scale
        canvas.style.transform = `scale(${scale})`;
        canvas.style.transformOrigin = 'center';

        // Ensure canvas overlays (Moveable / DragResizeRotateContainer) refresh after the canvas
        // scale/layout changes, otherwise borders can become misaligned until the next interaction.
        try {
            requestAnimationFrame(() => {
                window.dispatchEvent(new CustomEvent("updateHandlers"));
            });
        } catch {}
    };

    const compositionStyles = {
        veComposition: {
            flexGrow: 1,      // Allow it to grow and take available space
            flexShrink: 1,    // Allow shrinking if necessary
            minWidth: 0,
            height: '100%',   // Ensure it takes the full height of its container
            width: "100%",
            position: 'relative',
            minHeight: 0,
        },
        composition: {
            alignItems: 'center',
            // boxShadow: '0px 8px 32px 32px rgba(0, 0, 0, 0.04)',
            boxSizing: 'border-box',
            display: 'flex',
            flexGrow: 1,      // Allow it to grow and take available space in the flexbox
            height: '100%',
            justifyContent: 'center',
            padding: '16px 16px calc(16px + 14px)',
            position: 'relative',
            width: '100%',
            maxWidth: '100%',
            minHeight: 0,
        },
        compositionContainer: {
            position: 'relative',
            display: 'grid',
            height: '100%',
            placeContent: 'center',
            width: '100%',
            maxWidth: '100%',
            minHeight: 0,
            flexGrow: 1,
            alignItems: "center",
            justifyContent: "center",
        },
        canvas: {
            width: '100%',
            height: 'auto',
        },
        relative: {
            position: 'relative',
        },
    };

    return (
        <>
            {/* @ts-ignore */}
            <div style={compositionStyles.veComposition}>
                {/* @ts-ignore */}
                <div style={compositionStyles.composition} id={COMPOSITION_CONTAINER_ID} onMouseDown={handleMouseDown}>
                    {/* @ts-ignore */}
                    <div style={compositionStyles.compositionContainer} ref={containerRef}>
                        {/* @ts-ignore */}
                        <div style={compositionStyles.relative}
                             onMouseDown={(e) => {
                                 e.preventDefault();
                                 e.stopPropagation();
                             }}
                        >
                            <canvas id={CANVAS_ID} ref={canvasRef} style={compositionStyles.canvas}></canvas>
                            <DragResizeRotateContainer/>
                            <CropContainer onApplyCrop={handleApplyCrop} onDoneCrop={onCropDoneCapture}/>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
});

export default CompositionCanvasSection;
